import { QueryClient, QueryClientProvider } from 'react-query';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import UserContextProvider from '~/hooks/providers/UserContextProvider';
import LoginForm from '~/features/user/login/LoginForm';
import Verify2FA from '~/features/user/login/Verify2FA';
import Setup2FA from '~/features/user/login/Setup2FA';
import ForgotPasswordForm from '~/features/user/login/ForgotPasswordForm';
import ResetPasswordForm from '~/features/user/login/ResetPasswordForm';
import UserSignup from '~/features/user/signup/UserSignup';
import Acknowledgements from '~/routes/Acknowledgements';
import ProjectSearch from '~/features/projectSearch';
import { BrowserRouter as Router, Redirect, Route, Switch, useHistory } from 'react-router-dom';
import AuthRoute from '~/components/AuthRoute';
import Portfolio from '~/routes/Portfolio';
import Project from '~/routes/Project';
import PricingEstimate from './routes/PricingEstimate';
import InviteSignup from '~/routes/InviteSignup';
import Marketplace from '~/routes/Marketplace';
import Dashboard from '~/routes/Dashboard';
import { UserRole } from '~/constants';
import { storage } from './utils';
import BreadcrumbsContextProvider from '~/hooks/providers/BreadcrumbsContextProvider';
import { useEffect, useRef } from 'react';
import ClientLibrary from '~/features/ClientLibrary';
import AdminUsers from './features/admin/users';
import UserSettings from './features/user/settings';
import ReactModal from 'react-modal';
import NotificationResolver from './routes/NotificationResolver';
import AdminCompanies from './features/admin/companies';
import EmailVerification from './features/user/emailVerification/EmailVerification';
import RunEmailVerification from './features/user/emailVerification/RunEmailVerification';
import useLandingToast from './hooks/useLandingToasts';
import Logout from './features/user/logout/Logout';
import initializeAnalytics from './utils/analytics/initializeAnalytics';
import CompanyProfile from './features/company/CompanyProfile';
import BuyerNetwork from './features/buyerNetwork/BuyerNetwork';
import EnvironmentWatermark from './components/EnvironmentWatermark';
import Bids from './routes/Bids';
import BuyerProfileOnboarding from './routes/BuyerProfileOnboarding';
import AdminUserActivity from './routes/AdminUserActivity';
import AdminIngestionLog from './routes/AdminIngestionLog';
import AdminNotificationLog from './routes/AdminNotificationLog';
import AdminMcpAccess from './routes/AdminMcpAccess';
import OAuthAuthorize from './routes/OAuthAuthorize';
import { CreateFromDocumentProgressProvider } from '~/features/createFromDocument/CreateFromDocumentProgressProvider';
import CreateFromDocumentToastNotifier from '~/features/createFromDocument/CreateFromDocumentToastNotifier';

initializeAnalytics();

function ScrollToTopOnRouteChange() {
  const history = useHistory();
  const currentRouteRef = useRef(history.location);

  const isSame = (path1, path2) => {
    return path1 === path2 || (
      // This is done because of how budget/comments tabs are distinct
      // routes for portfolios
      // TODO update these tabs to be query params similar to the project review page
      (path1?.endsWith('/budget') && path2?.endsWith('/comments')) ||
      (path1?.endsWith('/comments') && path2?.endsWith('/budget'))
    );
  };

  useEffect(() => {
    const unlisten = history.listen((route) => {
      if (!isSame(currentRouteRef?.current?.pathname, route?.pathname)) {
        window.scrollTo(0, 0);
      }
      currentRouteRef.current = route;
    });
    return () => {
      unlisten();
    };
  }, []);

  return (null);
}

function App() {
  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <AppRouter />
    </QueryClientProvider>
  );
}

function AppRouter() {
  useLandingToast();

  const loggedIn = !!storage.getStoredCurrentUser();
  ReactModal.setAppElement('body');

  //const isQA = isQaEnvironment();

  return (
    <div className="app-content">
      <EnvironmentWatermark />
      <UserContextProvider>
        <CreateFromDocumentProgressProvider>
          <BreadcrumbsContextProvider>
            <Router>
            <ScrollToTopOnRouteChange />
            <CreateFromDocumentToastNotifier />
            <Switch>

              {/* redirects */}
              <Redirect exact from="/" to="/listings" />
              <Redirect exact from="/signup" to="/dashboard" />
              <Redirect exact from="/worksheet/:id" to="/project/:id/worksheet" />
              <Redirect exact from="/worksheet/:id/:section" to="/project/:id/worksheet/:section" />
              <Redirect exact from="/dataroom/:id" to="/project/:id/dataroom" />
              <Redirect exact from="/dataroom/:id/doclist" to="/project/:id/dataroom/doclist" />

              {/* public routes */}
              <Route path="/login">{loggedIn ? <Redirect to={(new URLSearchParams(window.location.search))?.get('redirect_uri') || '/'} /> : <LoginForm />}</Route>
              <Route path="/verify-2fa"><Verify2FA /></Route>
              <Route path="/setup-2fa"><Setup2FA /></Route>
              <Route path="/logout"><Logout /></Route>

              <AuthRoute path="/confirm-email/:token" exact>
                <RunEmailVerification />
              </AuthRoute>
              <AuthRoute path="/confirm-email" exact>
                <EmailVerification />
              </AuthRoute>
              <Route path="/signup-investors">
                <GoogleReCaptchaProvider
                  reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                >
                  <UserSignup userType={UserRole.INVESTOR} />
                </GoogleReCaptchaProvider>
              </Route>
              <Route path="/signup-developer-EPC">
                <GoogleReCaptchaProvider
                  reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                >
                  <UserSignup userType={UserRole.INSTALLER} />
                </GoogleReCaptchaProvider>
              </Route>
              <Route path="/team/invite/:token">
                <GoogleReCaptchaProvider
                  reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                >
                  <InviteSignup />
                </GoogleReCaptchaProvider>
              </Route>
              <Route path="/forgot-password">
                <ForgotPasswordForm />
              </Route>
              <Route path="/reset-password/:token">
                <ResetPasswordForm />
              </Route>

              {/* authenticated routes */}
              <AuthRoute path="/bids">
                <Bids />
              </AuthRoute>
              <AuthRoute path="/acknowledgements">
                <Acknowledgements />
              </AuthRoute>
              <AuthRoute path="/settings">
                <UserSettings />
              </AuthRoute>
              <AuthRoute path="/users">
                <AdminUsers />
              </AuthRoute>
              <AuthRoute path="/companies">
                <AdminCompanies />
              </AuthRoute>
              <AuthRoute path="/admin/user-activity">
                <AdminUserActivity />
              </AuthRoute>
              <AuthRoute path="/admin/ingestion-log">
                <AdminIngestionLog />
              </AuthRoute>
              <AuthRoute path="/admin/notifications">
                <AdminNotificationLog />
              </AuthRoute>
              <AuthRoute path="/admin/mcp-access">
                <AdminMcpAccess />
              </AuthRoute>
              <AuthRoute path="/oauth/authorize">
                <OAuthAuthorize />
              </AuthRoute>
              <AuthRoute path="/buyer-profile/onboarding">
                <BuyerProfileOnboarding />
              </AuthRoute>
              <AuthRoute path="/company-profile/:id">
                <CompanyProfile />
              </AuthRoute>
              <AuthRoute path="/my-deals">
                <ProjectSearch />
              </AuthRoute>
              <AuthRoute path="/project/:id">
                <Project />
              </AuthRoute>
              <AuthRoute path="/pricing-estimate/:id">
                <PricingEstimate />
              </AuthRoute>
              <AuthRoute path="/portfolio/:id">
                <Portfolio />
              </AuthRoute>
              <AuthRoute path="/notification/:id">
                <NotificationResolver />
              </AuthRoute>
              <AuthRoute path="/listings">
                <Marketplace />
              </AuthRoute>
              <AuthRoute path="/buyer-network">
                <BuyerNetwork />
              </AuthRoute>
              <AuthRoute path="/dashboard">
                <Dashboard />
              </AuthRoute>

              {/* client libary */}
              <AuthRoute path="/client-library">
                <ClientLibrary />
              </AuthRoute>

            </Switch>
          </Router>
        </BreadcrumbsContextProvider>
        </CreateFromDocumentProgressProvider>
      </UserContextProvider>
    </div>
  );
}

export default App;
